import React, {useContext} from "react"
import {CSSObject, styled} from "@mui/material/styles"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import merge from "lodash/merge"
import {ContainerStyles, ReadMoreStyles, TextStyles} from "./models"
import {Chevron} from "../../searchBannerChevron/components"
import {SearchBannerContext, removeUnsetProps} from "../utils"
import env from "../../../config/env"
import FadeInAnimation from "../../fadeInAnimation"

export const StyledButton = styled(Button, {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$readMoreStyles?: ReadMoreStyles}>(({theme, $readMoreStyles}) => {
    const override = removeUnsetProps({
        color: $readMoreStyles?.readMoreColor,
        textTransform: $readMoreStyles?.readMoreTextTransform,
    } as CSSObject)
    return merge(
        {
            // match size of text
            height: "1em",
            textWrap: "nowrap",
            minWidth: "auto",
            lineHeight: 1,
            alignItems: "baseline",
            color: theme.colours.plp.searchBanner.default.banner.copyText.link.primary.enabled.contrastTextColor,
            fontFamily: theme.colours.typography.body6.fontFamily,
            letterSpacing: theme.colours.typography.body6.letterSpacing,
            fontSize: theme.colours.typography.body6.fontSize,
            fontWeight: theme.colours.typography.body6.fontWeight,
            flexShrink: 0,
            backgroundColor: "transparent",
            "&:hover": {
                backgroundColor: "transparent",
            },
        } as CSSObject,
        override,
    )
})

export const StyledTypography = styled(Typography, {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$noWrap: boolean; $textStyles: TextStyles}>(({theme, $textStyles, $noWrap}) => {
    const clamp = $noWrap ? {WebkitLineClamp: $textStyles.lineClamp != null ? $textStyles.lineClamp : 1} : {}
    const override = removeUnsetProps({
        fontSize: $textStyles?.fontSize,
        fontWeight: $textStyles?.fontWeight,
        textAlign: $textStyles?.textAlign,
        color: $textStyles?.textColor,
        ...clamp,
    } as CSSObject)
    return merge(
        {
            color: theme.colours.plp.searchBanner.default.banner.copyText.textColor,
            fontFamily: theme.colours.typography.body1.fontFamily,
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            textOverflow: "ellipsis",
            overflow: "hidden",
            width: "100%",
            whiteSpace: "pre-line",
        } as CSSObject,
        override,
    )
})

export const CopyContainer = styled("div", {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$containerStyles: ContainerStyles; $disableChevron: boolean}>(({theme, $containerStyles, $disableChevron}) => {
    const override = removeUnsetProps({
        backgroundColor: $containerStyles.backgroundColor,
        flexDirection: $containerStyles.flexDirection,
        alignItems: $containerStyles.alignItems,
    } as CSSObject)
    return merge(
        {
            display: "flex",
            paddingRight: "1rem",
            paddingLeft: "1rem",
            paddingBottom: "1rem",
            [theme.breakpoints.down("md")]: {
                display: $disableChevron ? "flex" : "none",
            },
        },
        override,
    )
})

export const AccordionCopyContainer = styled("div", {
    shouldForwardProp: prop => !String(prop).startsWith("$"),
})<{$containerStyles: ContainerStyles; $isExpanded: boolean; $showAccordionOnDesktop: boolean}>(
    ({theme, $containerStyles, $isExpanded, $showAccordionOnDesktop}) => {
        const override = removeUnsetProps({
            backgroundColor: $containerStyles.backgroundColor,
        } as CSSObject)
        return merge(
            {
                paddingRight: "1rem",
                paddingLeft: "1rem",
                paddingBottom: $isExpanded ? "1rem" : "0",
                textAlign: "center",
                opacity: $isExpanded ? 1 : 0,
                transition: "opacity 0.5s ease-out",
                visibility: $isExpanded ? "visible" : "hidden",
                [theme.breakpoints.up("md")]: {
                    display: $showAccordionOnDesktop ? "block" : "none",
                },
                [theme.breakpoints.up("lg")]: {
                    paddingTop: $isExpanded ? "1rem" : "0",
                    backgroundColor: theme.colours.plp.searchBanner.default.banner.root.backgroundColor,
                },
            } as CSSObject,
            override,
        )
    },
)

export const MockPLPHeaderContainer = styled("div")({
    display: "flex",
    justifyContent: "end",
    paddingBottom: "0.75rem",
})

export const ChevronWithContext = () => {
    const {isExpanded, toggleIsExpanded, hasChevron} = useContext(SearchBannerContext)
    return (
        <FadeInAnimation timeout={300} show={hasChevron}>
            <Chevron
                src={`${env.REACT_APP_BLOB_STORAGE_PATH}/icons/shared/chevron.svg`}
                data-testid="plp-search-banner-chevron"
                onClick={toggleIsExpanded}
                $isOpen={isExpanded}
                alt={isExpanded ? "Collapse read more" : "Expand read more"}
                $isAtTop
                $isStandard
            />
        </FadeInAnimation>
    )
}

export const ColumnContainer = styled("div")`
    display: flex;
    flex-direction: column;
`
